import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import AuthContext from '../../contexts/AuthProvider';
import { API_URL } from '../../utils/config';
import EmptyState from './EmptyState';
import DeleteConfirmationModal from './DeleteConfirmationModal';
import OrganizerPagination from './OrganizerPagination';
import OrganizerRenderPaginationNumbers from './OrganizerRenderPaginationNumbers';
import './OrganizersTable.css';

const OrganizersTable = () => {
  const { t } = useTranslation('organizers');
  const navigate = useNavigate();
  const { isAuthenticated } = useContext(AuthContext);
  const [organizers, setOrganizers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [filteredOrganizers, setFilteredOrganizers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deleteModal, setDeleteModal] = useState({
    isOpen: false,
    organizerId: null,
    organizerName: '',
    isDeleting: false
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const itemsPerPage = 10;

  const baseUrl = API_URL;

  const getLangFromLocalStorage = () => {
    return localStorage.getItem('language') || 'en';
  };

  const getFullName = (organizer) => {
    if (organizer.name) {
      return organizer.name; // For backward compatibility
    }
    return `${organizer.fisrt_name || ''} ${organizer.family_name || ''}`.trim();
  };

  const handlePageChange = (pageNumber) => {
    if (pageNumber >= 1 && pageNumber <= totalPages) {
      setCurrentPage(pageNumber);
      // Update URL params
      const urlParams = new URLSearchParams(window.location.search);
      urlParams.set('page', pageNumber.toString());
      window.history.replaceState(null, '', `?${urlParams.toString()}`);
    }
  };

  // Get page from URL params on component mount
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const page = parseInt(urlParams.get('page')) || 1;
    setCurrentPage(page);
  }, []);

  // Fetch organizers from API
  const fetchOrganizers = async () => {
    try {
      setIsLoading(true);
      setError(null);

      // Don't navigate here, let ProtectedRoute handle authentication
      if (!isAuthenticated) {
        setIsLoading(false);
        return;
      }

      const params = new URLSearchParams({
        limit: itemsPerPage.toString(),
        page: currentPage.toString(),
      });

      const response = await fetch(`${baseUrl}/api/v1/attnd-venue/organizer/all?${params}`, {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'lang': getLangFromLocalStorage(),
        },
      });

      if (response.status === 401) {
        // localStorage.removeItem('user');
        // localStorage.removeItem('token');
        // Let ProtectedRoute handle the redirect instead of navigating here
        setIsLoading(false);
        throw new Error('Unauthorized access. Please log in again.');
      }

      if (!response.ok) {
        throw new Error(`Failed to fetch organizers: ${response.status}`);
      }

      const data = await response.json();
      console.log('API Response:', data); // Debug log
      // Handle different response formats
      const organizersArray = data.data || data.organizers || data || [];
      console.log('Organizers Array:', organizersArray); // Debug log
      setOrganizers(Array.isArray(organizersArray) ? organizersArray : []);
      setFilteredOrganizers(Array.isArray(organizersArray) ? organizersArray : []);
      
      // Set pagination data
      setTotalPages(data.totalPages || 1);
      setTotalItems(data.totalItems || 0);
      setCurrentPage(data.currentPage || currentPage);

    } catch (error) {
      console.error('Error fetching organizers:', error);
      setError(error.message);
      // For development, you can use sample data as fallback
      if (process.env.NODE_ENV === 'development') {
        const sampleData = [
          {
            id: 1,
            first_name: 'Ahmed',
            family_name: 'Hassan',
            email: 'ahmed.hassan@example.com',
            role: 'manager',
            teamOwner: 'Development Team'
          },
          {
            id: 2,
            first_name: 'Sara',
            family_name: 'Ali',
            email: 'sara.ali@example.com',
            role: 'viewer',
            teamOwner: 'Marketing Team'
          }
        ];
        setOrganizers(sampleData);
        setFilteredOrganizers(sampleData);
        setError(null);
      }
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (isAuthenticated) {
      fetchOrganizers();
    } else {
      // Clear data when not authenticated
      setOrganizers([]);
      setFilteredOrganizers([]);
      setIsLoading(false);
    }
  }, [isAuthenticated, currentPage]);

  // Filter organizers based on search term and role filter
  // Note: With server-side pagination, filtering should ideally be done on the server
  // For now, we'll filter the current page results only
  useEffect(() => {
    console.log('Filtering organizers:', { organizers, searchTerm, roleFilter }); // Debug log
    let filtered = Array.isArray(organizers) ? organizers : [];

    // Only apply local filtering if we have search term or role filter
    // In a full implementation, these should be sent to the server
    if (searchTerm || roleFilter !== 'all') {
      // Filter by search term
      if (searchTerm) {
        filtered = filtered.filter(organizer => {
          const fullName = getFullName(organizer);
          return fullName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                 organizer.email?.toLowerCase().includes(searchTerm.toLowerCase());
        });
      }

      // Filter by role
      if (roleFilter !== 'all') {
        filtered = filtered.filter(organizer => organizer.role === roleFilter);
      }
    }

    console.log('Filtered result:', filtered); // Debug log
    setFilteredOrganizers(Array.isArray(filtered) ? filtered : []);
  }, [organizers, searchTerm, roleFilter]);

  const handleEdit = (organizerId) => {
    navigate(`/organizers/edit/${organizerId}`);
  };

  const handleDelete = (organizer) => {
    setDeleteModal({
      isOpen: true,
      organizerId: organizer.id,
      organizerName: getFullName(organizer),
      isDeleting: false
    });
  };

  const handleDeleteConfirm = async () => {
    const { organizerId } = deleteModal;
    
    setDeleteModal(prev => ({ ...prev, isDeleting: true }));
    
    try {
      const response = await fetch(`${baseUrl}/api/v1/attnd-venue/organizer/${organizerId}`, {
        method: 'DELETE',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'lang': getLangFromLocalStorage(),
        },
      });

      if (!response.ok) {
        throw new Error('Failed to delete organizer');
      }

      // Remove from local state on successful deletion
      const updatedOrganizers = organizers.filter(org => org.id !== organizerId);
      setOrganizers(updatedOrganizers);
      
      // Close modal
      setDeleteModal({
        isOpen: false,
        organizerId: null,
        organizerName: '',
        isDeleting: false
      });
      
      console.log('Organizer deleted successfully');
      
    } catch (error) {
      console.error('Error deleting organizer:', error);
      // Keep modal open but stop loading state
      setDeleteModal(prev => ({ ...prev, isDeleting: false }));
      alert(t('deleteFailedMessage'));
    }
  };

  const handleDeleteCancel = () => {
    setDeleteModal({
      isOpen: false,
      organizerId: null,
      organizerName: '',
      isDeleting: false
    });
  };

  const handleAddOrganizer = () => {
    navigate('/organizers/add');
  };

  return (
    <div className="organizers-container">
      {/* Title */}
      <div className="organizers-header">
        <h1 className="organizers-title">{t('organizers')}</h1>
      </div>

      {/* Controls Row */}
      <div className="organizers-controls">
        <div className="controls-left">
          <h2 className="all-organizers-title">{t('allOrganizers')}</h2>
        </div>
        
        <div className="controls-right">
          {/* Search Bar */}
          <div className="search-container">
            <input
              type="text"
              placeholder={t('searchOrganizers')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input-org"
            />
            <span className='search-icon'>
                          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
  <path d="M11 20.75C5.62 20.75 1.25 16.38 1.25 11C1.25 5.62 5.62 1.25 11 1.25C16.38 1.25 20.75 5.62 20.75 11C20.75 16.38 16.38 20.75 11 20.75ZM11 2.75C6.45 2.75 2.75 6.45 2.75 11C2.75 15.55 6.45 19.25 11 19.25C15.55 19.25 19.25 15.55 19.25 11C19.25 6.45 15.55 2.75 11 2.75Z" fill="#464646"/>
  <path d="M20.16 22.79C20.08 22.79 20 22.78 19.93 22.77C19.46 22.71 18.61 22.39 18.13 20.96C17.88 20.21 17.97 19.46 18.38 18.89C18.79 18.32 19.48 18 20.27 18C21.29 18 22.09 18.39 22.45 19.08C22.81 19.77 22.71 20.65 22.14 21.5C21.43 22.57 20.66 22.79 20.16 22.79ZM19.56 20.49C19.73 21.01 19.97 21.27 20.13 21.29C20.29 21.31 20.59 21.12 20.9 20.67C21.19 20.24 21.21 19.93 21.14 19.79C21.07 19.65 20.79 19.5 20.27 19.5C19.96 19.5 19.73 19.6 19.6 19.77C19.48 19.94 19.46 20.2 19.56 20.49Z" fill="#464646"/>
</svg>
            </span>

            {/* <img src="/search.svg" alt={t('search')} className="search-icon" /> */}
          </div>

          {/* Role Filter */}
          <select
            value={roleFilter}
            onChange={(e) => setRoleFilter(e.target.value)}
            className="role-filter"
          >
            <option value="all">{t('allRoles')}</option>
            <option value="manager">{t('manager')}</option>
            <option value="viewer">{t('viewer')}</option>
          </select>

          {/* Add Organizer Button */}
          <button onClick={handleAddOrganizer} className="add-organizer-btn">
            {/* <img src="/plus.svg" alt={t('add')} className="add-icon" /> */}
            {t('addOrganizer')}
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
  <path d="M13.3334 10.625H6.66675C6.32508 10.625 6.04175 10.3417 6.04175 10C6.04175 9.65833 6.32508 9.375 6.66675 9.375H13.3334C13.6751 9.375 13.9584 9.65833 13.9584 10C13.9584 10.3417 13.6751 10.625 13.3334 10.625Z" fill="white"/>
  <path d="M10 13.9583C9.65833 13.9583 9.375 13.675 9.375 13.3333V6.66666C9.375 6.325 9.65833 6.04166 10 6.04166C10.3417 6.04166 10.625 6.325 10.625 6.66666V13.3333C10.625 13.675 10.3417 13.9583 10 13.9583Z" fill="white"/>
  <path d="M12.5001 18.9583H7.50008C2.97508 18.9583 1.04175 17.025 1.04175 12.5V7.5C1.04175 2.975 2.97508 1.04166 7.50008 1.04166H12.5001C17.0251 1.04166 18.9584 2.975 18.9584 7.5V12.5C18.9584 17.025 17.0251 18.9583 12.5001 18.9583ZM7.50008 2.29166C3.65841 2.29166 2.29175 3.65833 2.29175 7.5V12.5C2.29175 16.3417 3.65841 17.7083 7.50008 17.7083H12.5001C16.3417 17.7083 17.7084 16.3417 17.7084 12.5V7.5C17.7084 3.65833 16.3417 2.29166 12.5001 2.29166H7.50008Z" fill="white"/>
</svg>  
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="table-container">
        {isLoading ? (
          <div className="loading-container">
            <div className="loading-spinner-container">
              <img src="/loader.svg" alt={t('loading')} className="loading-spinner" />
              <p>{t('loadingOrganizers')}</p>
            </div>
          </div>
        ) : error ? (
          <div className="error-container">
            <div className="error-content">
              <img src="/error.svg" alt={t('error')} className="error-icon" />
              <h3>{t('failedToLoadOrganizers')}</h3>
              <p>{error}</p>
              <button onClick={fetchOrganizers} className="retry-btn">
                {t('tryAgain')}
              </button>
            </div>
          </div>
        ) : organizers.length === 0 ? (
          <EmptyState 
            icon="/empty-organizers.svg"
            title={t('noOrganizersYet')}
            description={t('noOrganizersDescription')}
            actionButton={
              <button onClick={handleAddOrganizer} className="empty-action-btn">
                <img src="/plus.svg" alt={t('add')} className="add-icon" />
                {t('addYourFirstOrganizer')}
              </button>
            }
          />
        ) : filteredOrganizers.length === 0 ? (
          <EmptyState 
            isSearch={true}
            searchTerm={searchTerm || (roleFilter !== 'all' ? `role: ${roleFilter}` : '')}
          />
        ) : (
          <table className="organizers-table">
            <thead>
              <tr>
                <th>{t('name')}</th>
                <th>{t('email')}</th>
                <th>{t('role')}</th>
                <th>{t('actions')}</th>
              </tr>
            </thead>
            <tbody>
              {Array.isArray(filteredOrganizers) && filteredOrganizers.map((organizer) => (
                <tr key={organizer.id}>
                  <td className="organizer-name">{getFullName(organizer)}</td>
                  <td className="organizer-email">{organizer.email}</td>
                  <td>
                    <span className={`role-badge role-${organizer.role}`}>
                      {organizer.role === 'manager' ? t('manager') : t('viewer')}
                    </span>
                  </td>
                  <td className="actions">
                    <button
                      onClick={() => handleEdit(organizer.id)}
                      className="action-btn edit-btn"
                      title={t('edit')}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                        <path d="M15 22.75H9C3.57 22.75 1.25 20.43 1.25 15V9C1.25 3.57 3.57 1.25 9 1.25H11C11.41 1.25 11.75 1.59 11.75 2C11.75 2.41 11.41 2.75 11 2.75H9C4.39 2.75 2.75 4.39 2.75 9V15C2.75 19.61 4.39 21.25 9 21.25H15C19.61 21.25 21.25 19.61 21.25 15V13C21.25 12.59 21.59 12.25 22 12.25C22.41 12.25 22.75 12.59 22.75 13V15C22.75 20.43 20.43 22.75 15 22.75Z" fill="#5507CF"/>
                        <path d="M8.50008 17.69C7.89008 17.69 7.33008 17.47 6.92008 17.07C6.43008 16.58 6.22008 15.87 6.33008 15.12L6.76008 12.11C6.84008 11.53 7.22008 10.78 7.63008 10.37L15.5101 2.49C17.5001 0.5 19.5201 0.5 21.5101 2.49C22.6001 3.58 23.0901 4.69 22.9901 5.8C22.9001 6.7 22.4201 7.58 21.5101 8.48L13.6301 16.36C13.2201 16.77 12.4701 17.15 11.8901 17.23L8.88008 17.66C8.75008 17.69 8.62008 17.69 8.50008 17.69ZM16.5701 3.55L8.69008 11.43C8.50008 11.62 8.28008 12.06 8.24008 12.32L7.81008 15.33C7.77008 15.62 7.83008 15.86 7.98008 16.01C8.13008 16.16 8.37008 16.22 8.66008 16.18L11.6701 15.75C11.9301 15.71 12.3801 15.49 12.5601 15.3L20.4401 7.42C21.0901 6.77 21.4301 6.19 21.4801 5.65C21.5401 5 21.2001 4.31 20.4401 3.54C18.8401 1.94 17.7401 2.39 16.5701 3.55Z" fill="#5507CF"/>
                        <path d="M19.8501 9.83C19.7801 9.83 19.7101 9.82 19.6501 9.8C17.0201 9.06 14.9301 6.97 14.1901 4.34C14.0801 3.94 14.3101 3.53 14.7101 3.41C15.1101 3.3 15.5201 3.53 15.6301 3.93C16.2301 6.06 17.9201 7.75 20.0501 8.35C20.4501 8.46 20.6801 8.88 20.5701 9.28C20.4801 9.62 20.1801 9.83 19.8501 9.83Z" fill="#5507CF"/>
                      </svg>
                    </button>
                    <button
                      onClick={() => handleDelete(organizer)}
                      className="action-btn delete-btn"
                      title={t('delete')}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                        <path d="M20.9999 6.73C20.9799 6.73 20.9499 6.73 20.9199 6.73C15.6299 6.2 10.3499 6 5.11992 6.53L3.07992 6.73C2.65992 6.77 2.28992 6.47 2.24992 6.05C2.20992 5.63 2.50992 5.27 2.91992 5.23L4.95992 5.03C10.2799 4.49 15.6699 4.7 21.0699 5.23C21.4799 5.27 21.7799 5.64 21.7399 6.05C21.7099 6.44 21.3799 6.73 20.9999 6.73Z" fill="#DC3545"/>
                        <path d="M8.50001 5.72C8.46001 5.72 8.42001 5.72 8.37001 5.71C7.97001 5.64 7.69001 5.25 7.76001 4.85L7.98001 3.54C8.14001 2.58 8.36001 1.25 10.69 1.25H13.31C15.65 1.25 15.87 2.63 16.02 3.55L16.24 4.85C16.31 5.26 16.03 5.65 15.63 5.71C15.22 5.78 14.83 5.5 14.77 5.1L14.55 3.8C14.41 2.93 14.38 2.76 13.32 2.76H10.7C9.64001 2.76 9.62001 2.9 9.47001 3.79L9.24001 5.09C9.18001 5.46 8.86001 5.72 8.50001 5.72Z" fill="#DC3545"/>
                        <path d="M15.2099 22.75H8.7899C5.2999 22.75 5.1599 20.82 5.0499 19.26L4.3999 9.19C4.3699 8.78 4.6899 8.42 5.0999 8.39C5.5199 8.37 5.8699 8.68 5.8999 9.09L6.5499 19.16C6.6599 20.68 6.6999 21.25 8.7899 21.25H15.2099C17.3099 21.25 17.3499 20.68 17.4499 19.16L18.0999 9.09C18.1299 8.68 18.4899 8.37 18.8999 8.39C19.3099 8.42 19.6299 8.77 19.5999 9.19L18.9499 19.26C18.8399 20.82 18.6999 22.75 15.2099 22.75Z" fill="#DC3545"/>
                        <path d="M13.6601 17.25H10.3301C9.92008 17.25 9.58008 16.91 9.58008 16.5C9.58008 16.09 9.92008 15.75 10.3301 15.75H13.6601C14.0701 15.75 14.4101 16.09 14.4101 16.5C14.4101 16.91 14.0701 17.25 13.6601 17.25Z" fill="#DC3545"/>
                        <path d="M14.5 13.25H9.5C9.09 13.25 8.75 12.91 8.75 12.5C8.75 12.09 9.09 11.75 9.5 11.75H14.5C14.91 11.75 15.25 12.09 15.25 12.5C15.25 12.91 14.91 13.25 14.5 13.25Z" fill="#DC3545"/>
                      </svg>
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination */}
      {organizers.length > 0 && totalPages > 1 && (
        <OrganizerPagination
          totalPages={totalPages}
          currentPage={currentPage}
          handlePageChange={handlePageChange}
        >
          <OrganizerRenderPaginationNumbers
            currentPage={currentPage}
            totalPages={totalPages}
            handlePageChange={handlePageChange}
          />
        </OrganizerPagination>
      )}

      {/* Delete Confirmation Modal */}
      <DeleteConfirmationModal
        isOpen={deleteModal.isOpen}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        itemName={deleteModal.organizerName}
        isLoading={deleteModal.isDeleting}
      />
    </div>
  );
};

export default OrganizersTable;